import React, { useEffect } from 'react';
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Beaker, BarChart3, Compass, BookOpen, Laptop, Lightbulb, Heart } from "lucide-react";
import { Link } from "wouter";
import Footer from "@/components/layout/Footer";
import { SectionHeading } from "@/components/ui/section-heading";
import { ContentCard } from "@/components/ui/content-card";
import midiLogo from "@/assets/midi-logo.png";
import cleanMarketLogo from "@/assets/clean-market-logo.png";
import superpowerLogo from "@/assets/superpower-logo.png";

export default function Consulting() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "//embed.typeform.com/next/embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Attempt to clean up Typeform's dynamically added elements
      const tfWidgets = document.querySelectorAll('div[data-tf-widget], iframe[data-tf-widget]');
      tfWidgets.forEach(widget => widget.remove());
    };
  }, []); // Empty dependency array ensures this runs once on mount and unmount

  const scrollToTypeform = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const typeformSection = document.getElementById('discovery-call-form');
    if (typeformSection) {
      typeformSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans antialiased">
      <Navbar />

      {/* Hero Section - Professional consulting style */}
      <section className="py-16 lg:py-24 bg-white text-black relative">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-8 md:pr-8">
              <h1 className="text-3xl md:text-4xl font-serif font-medium mb-6 max-w-2xl leading-tight tracking-tight">
                <span className="text-indigo-950">Strategic</span> and <span className="text-indigo-950">operational excellence</span> for longevity medicine.
              </h1>
              
              <p className="text-base md:text-lg text-gray-700 mb-8 max-w-2xl leading-relaxed">
                Deep expertise in clinical strategy, operational infrastructure, and market positioning for innovative providers in the longevity medicine ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <Button 
                  onClick={scrollToTypeform} 
                  className="bg-indigo-950 text-white hover:bg-indigo-900 text-sm px-6 py-2 rounded-sm h-auto"
                >
                  Apply for a Discovery Call
                </Button>
              </div>
            </div>
            
            <div className="md:col-span-4">
              <div className="bg-gray-50 p-6 w-full flex flex-col justify-center border border-gray-200 border-l-2 border-l-indigo-950 shadow-sm">
                <p className="text-lg font-serif leading-snug mb-4 text-indigo-950">
                  "I help turn science into strategy—and strategy into systems that deliver measurable results."
                </p>
                <p className="text-sm font-medium tracking-wide text-gray-700">Dr. Anant Vinjamoori</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading 
            title="Who We Serve" 
            description="Tailored expertise for healthcare innovators at every stage of development."
            align="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Client Type 1 */}
            <ContentCard title="Individual Clinicians & Entrepreneurs">
              Guidance for practitioners seeking to build innovative, science-driven longevity practices from the ground up with limited resources and maximum impact.
            </ContentCard>
            
            {/* Client Type 2 */}
            <ContentCard title="Startup Clinics">
              Comprehensive support for emerging longevity clinics establishing their foundation in terms of clinical protocols, operations, and brand positioning.
            </ContentCard>
            
            {/* Client Type 3 */}
            <ContentCard title="Scaling Enterprises">
              Strategic partnership for established organizations seeking to scale operations, expand service lines, and optimize their impact in the longevity marketplace.
            </ContentCard>
          </div>
        </div>
      </section>
      
      {/* Capabilities Section - Professional consulting style */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading 
            title="Integrated Medical, Operational, and Brand Expertise" 
            description="A comprehensive approach that delivers transformative results for healthcare innovators."
            align="center"
            className="mb-10"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Capability 1 */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center">
                <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mr-3">01</p>
                <h3 className="text-base font-semibold text-gray-900">Strategic Leadership</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Fractional CMO/COO/CMO capabilities that provide executive-level insight without the overhead of multiple full-time hires.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Business model innovation</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Market positioning</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Capability 2 */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center">
                <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mr-3">02</p>
                <h3 className="text-base font-semibold text-gray-900">Clinical Excellence</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Evidence-based protocols and clinical frameworks that deliver safe, effective, and scalable longevity medicine.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Biomarker-driven protocols</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Treatment algorithms</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Capability 3 */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center">
                <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mr-3">03</p>
                <h3 className="text-base font-semibold text-gray-900">Operational Systems</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Scalable infrastructure and data-driven tools that transform insights into consistent, high-quality patient experiences.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>KPI dashboards</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Workflow optimization</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Experience Section - Professional consulting style */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading 
            title="Industry-Leading Experience in Healthcare Innovation" 
          />
          
          <div className="max-w-3xl">
            <p className="text-sm text-gray-700 leading-relaxed mb-6">
              I advise and consult with leading companies in longevity medicine—helping them build what actually works: scalable care models, high-impact protocols, and brands that stand out. My perspective draws on a rare blend of clinical depth, operational expertise, and strategic insight.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mb-6">
              I began my career as Director of Product at Virta Health, where I helped grow a startup into a multi-billion dollar business at the intersection of software and metabolic care. I then served as Chief Medical Officer at Modern Age, one of the first national-scale longevity clinics, where I translated science into real-world outcomes.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Now, I work with some of the most innovative companies in the space—including Midi and Superpower—guiding clinical design, operational infrastructure, and go-to-market strategy. Across every engagement, my focus is the same: turning the promise of longevity medicine into systems that deliver.
            </p>
          </div>
        </div>
      </section>
      
      {/* Services Section - Professional consulting style */}
      <section className="py-16 bg-indigo-950 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-8">
            <h2 className="text-2xl font-serif font-medium mb-2 text-center">
              Comprehensive Consulting Services
            </h2>
            <div className="h-px bg-indigo-300/30 w-16 mx-auto mb-4"></div>
            <p className="text-sm text-indigo-100 max-w-2xl mx-auto text-center mb-12">
              End-to-end support for founders and clinics at every stage of development.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Service 1 */}
            <div className="bg-indigo-900/50 border border-indigo-800 p-6 hover:bg-indigo-900/70 transition-colors">
              <p className="text-[11px] uppercase tracking-wider text-indigo-300 font-medium mb-4">01</p>
              <h3 className="text-base font-medium mb-3">Strategic Advisory</h3>
              <p className="text-xs text-indigo-100 mb-4 leading-relaxed">
                Develop comprehensive business strategies and go-to-market plans that position your organization for long-term success.
              </p>
              <ul className="space-y-2 text-xs text-indigo-100">
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Business model design</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Go-to-market strategy</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Patient journey architecture</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Brand positioning</span>
                </li>
              </ul>
            </div>
            
            {/* Service 2 */}
            <div className="bg-indigo-900/50 border border-indigo-800 p-6 hover:bg-indigo-900/70 transition-colors">
              <p className="text-[11px] uppercase tracking-wider text-indigo-300 font-medium mb-4">02</p>
              <h3 className="text-base font-medium mb-3">Clinical Excellence</h3>
              <p className="text-xs text-indigo-100 mb-4 leading-relaxed">
                Establish evidence-based protocols and training systems that deliver consistent, high-quality patient outcomes.
              </p>
              <ul className="space-y-2 text-xs text-indigo-100">
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Evidence-based protocols</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Clinical SOPs</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Provider training systems</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Biomarker evaluation frameworks</span>
                </li>
              </ul>
            </div>
            
            {/* Service 3 */}
            <div className="bg-indigo-900/50 border border-indigo-800 p-6 hover:bg-indigo-900/70 transition-colors">
              <p className="text-[11px] uppercase tracking-wider text-indigo-300 font-medium mb-4">03</p>
              <h3 className="text-base font-medium mb-3">Operational Systems</h3>
              <p className="text-xs text-indigo-100 mb-4 leading-relaxed">
                Create scalable infrastructure and data-driven tools that transform your business from concept to scale.
              </p>
              <ul className="space-y-2 text-xs text-indigo-100">
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Performance dashboards</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Workflow optimization</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>Operations playbooks</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-0.5 text-indigo-300 text-xs">—</div>
                  <span>System integration</span>
                </li>
              </ul>
              {/* This button (formerly Notion link on line ~355) is updated */}
              <Button 
                onClick={scrollToTypeform} 
                variant="link"
                className="text-indigo-300 hover:text-white p-0 h-auto text-sm font-medium">
                Apply for a Discovery Call <ArrowRight className="ml-1.5 h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Engagement Models Section - Professional consulting style */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading 
            title="Engagement Models" 
            description="Flexible consulting options tailored to your organization's needs and stage of development."
            align="center"
            className="mb-10"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Model 1 */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mb-2">Standard</p>
                  <h3 className="text-base font-medium text-gray-900 mb-1">Advisory Access</h3>
                  <p className="text-xs text-gray-600">For teams seeking ongoing guidance</p>
                </div>
                <p className="text-sm font-medium text-indigo-900">$2,500<span className="text-xs font-normal">/month</span></p>
              </div>
              
              <div className="flex-grow mb-6">
                <p className="text-xs text-gray-700 mb-4 leading-relaxed">
                  Ongoing strategic guidance and ad-hoc support delivered through your preferred collaboration channels.
                </p>
                
                <ul className="space-y-2.5">
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Slack-based advisory support</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Unlimited questions and document reviews</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Monthly analysis of key metrics</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Add your entire team at no extra cost</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-auto pt-4 border-t border-gray-100">
                <Button 
                  onClick={scrollToTypeform} 
                  className="w-full bg-indigo-950 text-white hover:bg-indigo-900 text-xs px-4 py-2 rounded-sm h-auto">
                  Apply for a Discovery Call
                </Button>
              </div>
            </div>
            
            {/* Model 2 */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mb-2">Premium</p>
                  <h3 className="text-base font-medium text-gray-900 mb-1">Strategic Partnership</h3>
                  <p className="text-xs text-gray-600">For deeper operational collaboration</p>
                </div>
                <p className="text-sm font-medium text-indigo-900">Contact Us</p>
              </div>
              
              <div className="flex-grow mb-6">
                <p className="text-xs text-gray-700 mb-4 leading-relaxed">
                  Dedicated strategic leadership and hands-on support for scaling enterprises seeking transformative growth. May include PC ownership, advisory board membership, or board membership.
                </p>
                
                <ul className="space-y-2.5">
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Weekly strategic working sessions</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Fractional executive leadership</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Custom system development</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-2 mt-0.5 text-indigo-900 text-xs">—</div>
                    <span className="text-xs text-gray-700">Team capability development</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-auto pt-4 border-t border-gray-100">
                <Button 
                  onClick={scrollToTypeform} 
                  className="w-full bg-indigo-950 text-white hover:bg-indigo-900 text-xs px-4 py-2 rounded-sm h-auto">
                  Apply for a Discovery Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Trusted Partners Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading 
            title="The Best in the Business Trust Next Generation Medicine" 
            description="Advisory relationships with leading healthcare innovators."
            align="center"
            className="mb-10"
          />
          
          <div className="flex flex-row justify-center items-center max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-x-20 gap-y-10">
              {/* Midi Logo */}
              <div className="w-40 h-16 relative flex items-center justify-center">
                <div className="absolute inset-0 grayscale hover:grayscale-0 transition-all duration-300 flex items-center justify-center">
                  <img 
                    src={midiLogo} 
                    alt="Midi Health" 
                    className="max-h-12 w-auto object-contain" 
                  />
                </div>
              </div>
              
              {/* Clean Market Logo */}
              <div className="w-40 h-16 relative flex items-center justify-center">
                <div className="absolute inset-0 grayscale hover:grayscale-0 transition-all duration-300 flex items-center justify-center">
                  <img 
                    src={cleanMarketLogo} 
                    alt="Clean Market" 
                    className="max-h-8 w-auto object-contain" 
                  />
                </div>
              </div>
              
              {/* Superpower Logo */}
              <div className="w-48 h-16 relative flex items-center justify-center">
                <div className="absolute inset-0 grayscale hover:grayscale-0 transition-all duration-300 flex items-center justify-center">
                  <img 
                    src={superpowerLogo} 
                    alt="Superpower" 
                    className="max-h-10 w-auto object-contain" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section - Professional consulting style */}
      <section className="py-16 bg-indigo-950 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-serif font-medium mb-4">
              Ready to elevate your longevity practice?
            </h2>
            <div className="h-px bg-indigo-300/30 w-16 mx-auto mb-6"></div>
            <p className="text-sm text-indigo-100 mb-8 leading-relaxed">
              Schedule a discovery call to discuss how we can help you build, optimize, or scale your longevity medicine practice.
            </p>
            <Button 
              onClick={scrollToTypeform} 
              className="bg-white text-indigo-950 hover:bg-gray-100 text-sm px-6 py-2 rounded-sm">
              Apply for a Discovery Call
            </Button>
          </div>
        </div>
      </section>
      
      {/* Call to Action / Embedded Typeform Section Added Before Footer */}
      <section id="discovery-call-form" className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <SectionHeading 
            title="Ready to Elevate Your Practice?"
            description="Apply for a complimentary discovery call to discuss your goals and how we can help you achieve them."
            align="center"
            className="mb-10"
          />
          {/* Typeform Embedded Widget */}
          <div data-tf-live="01JTMDDGCP5TVAC8F11XQ5BKZR" style={{width: '100%', height: '600px', border: '1px solid #eee', borderRadius: '8px'}}></div>
        </div>
      </section>

      {/* Why Partner With Me Section - Professional consulting style */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading 
            title="Why Partner with NGM Consulting?"
            description="Leverage deep industry expertise to build a resilient and impactful longevity medicine practice."
            align="center"
            className="mb-10"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mb-12">
            {/* Benefit 1 */}
            <div className="p-2">
              <Lightbulb className="h-8 w-8 text-indigo-950 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 mb-1">Strategic Clarity</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Navigate the complexities of the longevity market with a clear, actionable roadmap.
              </p>
            </div>
            {/* Benefit 2 */}
            <div className="p-2">
              <Heart className="h-8 w-8 text-indigo-950 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 mb-1">Operational Efficiency</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Implement scalable systems that enhance patient care and business growth.
              </p>
            </div>
            {/* Benefit 3 */}
            <div className="p-2">
              <Target className="h-8 w-8 text-indigo-950 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 mb-1">Sustainable Impact</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Build a practice that not only thrives but also makes a meaningful difference in patient lives.
              </p>
            </div>
          </div>

          {/* This button (formerly Notion link on line ~400) is updated */}
          <div className="text-center mt-10">
            <Button 
              onClick={scrollToTypeform} 
              className="bg-indigo-950 text-white hover:bg-indigo-900 text-sm px-8 py-3 rounded-sm h-auto shadow-md hover:shadow-lg transition-shadow">
              Apply for a Discovery Call
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Professional consulting style */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <SectionHeading 
            title="Trusted by Innovators"
            align="center"
            className="mb-10"
          />
          <div className="space-y-8">
            {/* Testimonial 1 - Placeholder */}
            <blockquote className="p-6 bg-white border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-700 leading-relaxed mb-4 italic">
                "Dr. Vinjamoori's insights were pivotal in shaping our clinical strategy. His ability to bridge scientific depth with practical business application is unparalleled."
              </p>
              <footer className="text-xs text-gray-500">
                <span className="font-semibold text-gray-700">CEO, HealthTech Startup</span>
              </footer>
            </blockquote>
            {/* Testimonial 2 - Placeholder */}
            <blockquote className="p-6 bg-white border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-700 leading-relaxed mb-4 italic">
                "The operational frameworks we developed with Anant's guidance allowed us to scale our services efficiently while maintaining the highest standards of care."
              </p>
              <footer className="text-xs text-gray-500">
                <span className="font-semibold text-gray-700">Founder, Boutique Longevity Clinic</span>
              </footer>
            </blockquote>
          </div>
          {/* This link (formerly Notion link on line ~471) is updated. Using a Button component styled as a link for consistency. */}
          <div className="mt-10 text-center">
            <Button 
              onClick={scrollToTypeform} 
              variant="link" 
              className="text-indigo-950 hover:text-indigo-700 text-sm font-medium">
              Apply for a Discovery Call <ArrowRight className="ml-1.5 h-3 w-3" />
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}